import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, intlShape, injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import log from '../lib/log';
import sharedMessages from './shared-messages';
import closed from '../lib/assets/close.svg';
import prestyle from '../css/prefile.css';

import {
    LoadingStates,
    getIsLoadingUpload,
    getIsShowingWithoutId,
    onLoadedProject,
    requestProjectUpload
} from '../reducers/project-state';
import {setProjectTitle} from '../reducers/project-title';
import {
    openLoadingProject,
    closeLoadingProject
} from '../reducers/modals';
import {
    closeFileMenu
} from '../reducers/menus';


const messages = defineMessages({
    loadError: {
        id: 'gui.projectLoader.loadError',
        defaultMessage: 'The project file that was selected failed to load.',
        description: 'An error that displays when a local project file fails to load.'
    }
});

/**
 * Higher Order Component to provide behavior for loading local project files into editor.
 * @param {React.Component} WrappedComponent the component to add project file loading functionality to
 * @returns {React.Component} WrappedComponent with project file loading functionality added
 *
 * <SBFileUploaderHOC>
 *     <WrappedComponent />
 * </SBFileUploaderHOC>
 */
const SBFileUploaderHOC = function (WrappedComponent) {
    class SBFileUploaderComponent extends React.Component {
        constructor (props) {
            super(props);
            this.state = {
                scratchBaseUrl: 'https://frog-backend.youngcode.net',
                userName: '',
                files: [], // 用于存储文件列表
                showModal: false // 用于控制弹窗显示状态
            };
            bindAll(this, [
                'createFileObjects',
                'getProjectTitleFromFilename',
                'handleFinishedLoadingUpload',
                'handleStartSelectingFileUpload',
                'handleChange',
                'onload',
                'removeFileObjects',
                'closeModal',
                'fetchFileList',
                // 'openLocal',
                'handleChangeLocal',
                'removeUserFile'
            ]);
        }

        componentDidUpdate (prevProps) {
            if (this.props.isLoadingUpload && !prevProps.isLoadingUpload) {
                this.handleFinishedLoadingUpload(); // cue step 5 below
            }
        }

        componentWillUnmount () {
            this.removeFileObjects();
        }

        // 从本电脑中打开
        // openLocal (){
        //     this.closeModal();
        //     // this.setState({showModal: false});
        //     this.removeFileObjects();
        //
        //     this.fileReader = new FileReader();
        //     this.fileReader.onload = this.onload;
        //     this.inputElement = document.createElement('input');
        //     this.inputElement.accept = '.sb,.sb2,.sb3';
        //     this.inputElement.style = 'display: none;';
        //     this.inputElement.type = 'file';
        //     this.inputElement.onchange = this.handleChangeLocal;
        //     document.body.appendChild(this.inputElement);
        //     this.inputElement.click();
        // }

        // step 1: this is where the upload process begins
        handleStartSelectingFileUpload (isLocalFile) {
            this.createFileObjects(isLocalFile); // go to step 2
        }

        // step 2: create a FileReader and an <input> element, and issue a
        // pseudo-click to it. That will open the file chooser dialog.
        createFileObjects (isLocalFile) {
            this.removeFileObjects();

            if (isLocalFile){
                this.fileReader = new FileReader();
                this.fileReader.onload = this.onload;
                this.inputElement = document.createElement('input');
                this.inputElement.accept = '.sb,.sb2,.sb3';
                this.inputElement.style = 'display: none;';
                this.inputElement.type = 'file';
                this.inputElement.onchange = this.handleChangeLocal;
                document.body.appendChild(this.inputElement);
                this.inputElement.click();
            } else {
                this.fetchFileList();
            }

        }

        fetchFileList () {
            const userId = sessionStorage.getItem('userId') || '18712495260';
            const userName = sessionStorage.getItem('userName') || userId;
            fetch(`${this.state.scratchBaseUrl}/scratch/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({userId}) // 替换为实际的请求参数
            })
                .then(response => response.json())
                .then(res => {
                    this.setState({files: res.data || [], showModal: true, userName});
                })
                .catch(error => {
                    console.error('Error fetching files:', error);
                });
        }

        // step 3: 用户已经使用文件选择器对话框选择了一个文件
        // 我们实际上并没有在这里加载文件，我们只是决定是否加载。
        handleChangeLocal (e) {
            const {
                intl,
                isShowingWithoutId,
                loadingState,
                projectChanged,
                userOwnsProject
            } = this.props;
            const thisFileInput = e.target;
            if (thisFileInput.files) { // 如果没有选择文件，不要尝试加载
                this.fileToUpload = thisFileInput.files[0];

                let uploadAllowed = true;
                if (userOwnsProject || (projectChanged && isShowingWithoutId)) {
                    uploadAllowed = confirm( // eslint-disable-line no-alert
                        intl.formatMessage(sharedMessages.replaceProjectWarning)
                    );
                }
                if (uploadAllowed) {
                    // cues step 4
                    this.props.requestProjectUpload(loadingState);
                } else {
                    // skips ahead to step 7
                    this.removeFileObjects();
                }
                this.props.closeFileMenu();
            }
        }

        // 删除用户列表
        removeUserFile (file){
            let allowRemove = false;
            allowRemove = confirm( // eslint-disable-line no-alert
                this.props.intl.formatMessage(sharedMessages.removeFile)
            );
            if (allowRemove){

                fetch(`${this.state.scratchBaseUrl}/scratch/removeById?id=${file.id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                })
                    .then(response => {
                        if (response.ok){
                            this.fetchFileList();
                        } else {
                            // eslint-disable-next-line no-alert
                            alert('删除时发生错误，请重试。');
                        }
                    })
                    .catch(error => {
                        console.error('Error fetching files:', error);
                    });
            }
        }

        handleChange (file) {
            const url = `${this.state.scratchBaseUrl}/scratch/downloadFile?id=${file.id}`;
            try {
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    }
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        return response.blob(); // 2. 将响应转换为 Blob 对象
                    })
                    .then(blob => {
                        const files = new File([blob], file.fileName, {
                            type: 'application/octet-stream',
                            lastModified: Date.now()
                        });

                        const {
                            intl,
                            isShowingWithoutId,
                            loadingState,
                            projectChanged,
                            userOwnsProject
                        } = this.props;

                        this.fileToUpload = files;
                        this.fileReader = new FileReader();
                        this.fileReader.onload = this.onload;
                        this.fileReader.onerror = error => {
                            console.log('FileReader error:', error);
                            // eslint-disable-next-line no-alert
                            alert('加载文件内容时发生错误，请重试。');
                        };

                        let uploadAllowed = true;
                        if (userOwnsProject || (projectChanged && isShowingWithoutId)) {
                            uploadAllowed = confirm( // eslint-disable-line no-alert
                                intl.formatMessage(sharedMessages.replaceProjectWarning)
                            );
                        }
                        if (uploadAllowed) {
                            this.props.requestProjectUpload(loadingState);
                        } else {
                            this.removeFileObjects();
                        }
                        this.props.closeFileMenu();
                        this.setState({showModal: false});
                    });
                // .catch(error => {
                //     console.error('Error fetching file:', error);
                //     alert('从服务器获取文件时发生错误，请重试。');
                // });
            } catch (error) {
                console.error('Fetch error:', error);
                // eslint-disable-next-line no-alert
                alert('网络请求失败，请检查您的网络连接并重试。');
            }
        }

        // step 4 is below, in mapDispatchToProps

        // step 5: called from componentDidUpdate when project state shows
        // that project data has finished "uploading" into the browser
        handleFinishedLoadingUpload () {
            if (this.fileToUpload && this.fileReader) {
                this.fileReader.readAsArrayBuffer(this.fileToUpload);
            } else {
                this.props.cancelFileUpload(this.props.loadingState);
                this.removeFileObjects();
            }
        }

        getProjectTitleFromFilename (fileInputFilename) {
            if (!fileInputFilename) return '';
            const matches = fileInputFilename.match(/^(.*)\.sb[23]?$/);
            if (!matches) return '';
            return matches[1].substring(0, 100);
        }

        onload () {
            if (this.fileReader) {
                this.props.onLoadingStarted();
                const filename = this.fileToUpload && this.fileToUpload.name;
                let loadingSuccess = false;
                this.props.vm.loadProject(this.fileReader.result)
                    .then(() => {
                        if (filename) {
                            const uploadedProjectTitle = this.getProjectTitleFromFilename(filename);
                            this.props.onSetProjectTitle(uploadedProjectTitle);
                        }
                        loadingSuccess = true;
                    })
                    .catch(error => {
                        log.warn(error);
                        alert(this.props.intl.formatMessage(messages.loadError)); // eslint-disable-line no-alert
                    })
                    .then(() => {
                        this.props.onLoadingFinished(this.props.loadingState, loadingSuccess);
                        this.removeFileObjects();
                    });
            }
        }

        removeFileObjects () {
            if (this.inputElement) {
                this.inputElement.value = null;
                document.body.removeChild(this.inputElement);
            }
            this.inputElement = null;
            this.fileReader = null;
            this.fileToUpload = null;
        }

        closeModal () {
            this.setState({showModal: false});
        }

        render () {
            const {
                /* eslint-disable no-unused-vars */
                cancelFileUpload,
                closeFileMenu: closeFileMenuProp,
                isLoadingUpload,
                isShowingWithoutId,
                loadingState,
                onLoadingFinished,
                onLoadingStarted,
                onSetProjectTitle,
                projectChanged,
                requestProjectUpload: requestProjectUploadProp,
                userOwnsProject,
                /* eslint-enable no-unused-vars */
                ...componentProps
            } = this.props;

            return (
                <React.Fragment>
                    <WrappedComponent
                        onStartSelectingFileUpload={this.handleStartSelectingFileUpload}
                        {...componentProps}
                    />
                    {/* <button onClick={this.handleStartSelectingFileUpload}>Upload Files</button> */}
                    {this.state.showModal && (
                        <div
                            className={prestyle.modalStyle}
                        >
                            <div
                                className={prestyle.modalContentStyle}
                            >
                                <div
                                    className={prestyle.fileTop}
                                >
                                    <p>{this.state.userName}的作品列表</p>
                                    <img
                                        onClick={this.closeModal}
                                        src={closed}
                                        style={{cursor: 'pointer'}}
                                    />
                                </div>
                                {this.state.files.length > 0 ? (
                                    <ol className={prestyle.fileOl}>
                                        {this.state.files.map((file, index) => (
                                            <li
                                                key={index}
                                                className={prestyle.fileItem}
                                            >
                                                <p
                                                    className={prestyle.fileName}
                                                    onClick={() => this.handleChange(file)}
                                                >{file.fileName}</p>
                                                <p
                                                    className={prestyle.deleteIcon}
                                                    onClick={() => this.removeUserFile(file)}
                                                >删除</p>
                                            </li>
                                        ))}
                                    </ol>
                                ) : (
                                    <p className={prestyle.nullItem}>暂无保存作品</p>
                                )}
                            </div>
                        </div>
                    )}
                </React.Fragment>
            );
        }
    }

    SBFileUploaderComponent.propTypes = {
        canSave: PropTypes.bool,
        cancelFileUpload: PropTypes.func,
        closeFileMenu: PropTypes.func,
        intl: intlShape.isRequired,
        isLoadingUpload: PropTypes.bool,
        isShowingWithoutId: PropTypes.bool,
        loadingState: PropTypes.oneOf(LoadingStates),
        onLoadingFinished: PropTypes.func,
        onLoadingStarted: PropTypes.func,
        onSetProjectTitle: PropTypes.func,
        projectChanged: PropTypes.bool,
        requestProjectUpload: PropTypes.func,
        userOwnsProject: PropTypes.bool,
        vm: PropTypes.shape({
            loadProject: PropTypes.func
        })
    };

    const mapStateToProps = (state, ownProps) => {
        const loadingState = state.scratchGui.projectState.loadingState;
        const user = state.session && state.session.session && state.session.session.user;
        return {
            isLoadingUpload: getIsLoadingUpload(loadingState),
            isShowingWithoutId: getIsShowingWithoutId(loadingState),
            loadingState: loadingState,
            projectChanged: state.scratchGui.projectChanged,
            userOwnsProject: ownProps.authorUsername && user &&
                (ownProps.authorUsername === user.username),
            vm: state.scratchGui.vm
        };
    };

    const mapDispatchToProps = (dispatch, ownProps) => ({
        cancelFileUpload: loadingState => dispatch(onLoadedProject(loadingState, false, false)),
        closeFileMenu: () => dispatch(closeFileMenu()),
        onLoadingFinished: (loadingState, success) => {
            dispatch(onLoadedProject(loadingState, ownProps.canSave, success));
            dispatch(closeLoadingProject());
            dispatch(closeFileMenu());
        },
        onLoadingStarted: () => dispatch(openLoadingProject()),
        onSetProjectTitle: title => dispatch(setProjectTitle(title)),
        requestProjectUpload: loadingState => dispatch(requestProjectUpload(loadingState))
    });

    const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
        {}, stateProps, dispatchProps, ownProps
    );

    return injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(SBFileUploaderComponent));
};

export {
    SBFileUploaderHOC as default
};
